import { Plus, X, GripVertical } from "lucide-react";
import TopbarPfmFilterWidget from "./TopbarPfmFilterWidget";
import TopbarPhaseFilterWidget from "./TopbarPhaseFilterWidget";
import TopbarSymbolFilterWidget from "./TopbarSymbolFilterWidget";
import TopbarSideFilterWidget from "./TopbarSideFilterWidget";
import TopbarStatusFilterWidget from "./TopbarStatusFilterWidget";
import TopbarPnlFilterWidget from "./TopbarPnlFilterWidget";
import TopbarTagsFilterWidget from "./TopbarTagsFilterWidget";
import { Button } from "../../client/components/ui/button";
import { DateRangePicker } from "../../client/components/DateRangePicker";
import type { DateRange } from "../../client/utils/dateRange";
import { AccountGroupFilter } from "../../trading-accounts/AccountGroupFilter";
import type { AccountFilterValue } from "../../trading-accounts/AccountGroupFilter";
import StrategyMultiSelect from "../../client/components/StrategyMultiSelect";
import useTranslation from "../../client/hooks/useTranslation";
import type { SlotPlacement } from "./types";
import {
  serializeDateRange,
  serializeAccountFilter,
  serializeStrategyIds,
  serializePfmFilter,
  serializePhaseFilter,
} from "../../custom-pages/topbarDefaults";
import { useSortable, SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import type { ReactNode } from "react";

export interface TopbarSlotRendererProps {
  slot: SlotPlacement;
  isEditMode: boolean;
  dateRange: DateRange;
  accountFilter: AccountFilterValue;
  strategyIds: string[];
  accounts: any[];
  groups: any[];
  strategies: any[];
  onDateRangeChange: (r: DateRange) => void;
  onAccountFilterChange: (v: AccountFilterValue) => void;
  onStrategyIdsChange: (ids: string[]) => void;
  onAddTrade: () => void;
  onAddPayout: () => void;
  onRemove: () => void;
  onPin?: (value: string | null) => void;
  pfmTemplateFilter?: string | null;
  onPfmTemplateFilterChange?: (id: string | null) => void;
  allAccounts?: any[];
  accountStatusFilter?: string | null;
  onAccountStatusFilterChange?: (status: string | null) => void;
  // Journal filters
  journalSymbol?: string;
  onJournalSymbolChange?: (v: string) => void;
  journalSide?: "all" | "long" | "short";
  onJournalSideChange?: (v: "all" | "long" | "short") => void;
  journalStatus?: "all" | "open" | "closed";
  onJournalStatusChange?: (v: "all" | "open" | "closed") => void;
  journalMinPnl?: string;
  journalMaxPnl?: string;
  onJournalMinPnlChange?: (v: string) => void;
  onJournalMaxPnlChange?: (v: string) => void;
  journalTags?: any[];
  journalTagIds?: string[];
  onJournalTagIdsChange?: (ids: string[]) => void;
}

export default function TopbarSlotRenderer({
  slot,
  isEditMode,
  dateRange,
  accountFilter,
  strategyIds,
  accounts,
  groups,
  strategies,
  onDateRangeChange,
  onAccountFilterChange,
  onStrategyIdsChange,
  onAddTrade,
  onAddPayout,
  onRemove,
  onPin,
  pfmTemplateFilter = null,
  onPfmTemplateFilterChange,
  allAccounts,
  accountStatusFilter = null,
  onAccountStatusFilterChange,
  journalSymbol = "",
  onJournalSymbolChange,
  journalSide = "all",
  onJournalSideChange,
  journalStatus = "all",
  onJournalStatusChange,
  journalMinPnl = "",
  journalMaxPnl = "",
  onJournalMinPnlChange,
  onJournalMaxPnlChange,
  journalTags = [],
  journalTagIds = [],
  onJournalTagIdsChange,
}: TopbarSlotRendererProps) {
  const t = useTranslation();

  const savedDefault = slot.defaultFilterValue ?? null;
  const pinnedAccountFilter: AccountFilterValue | null = (() => {
    if (slot.widgetKey !== "topbar-account-filter" || !savedDefault) return null;
    try { return JSON.parse(savedDefault) as AccountFilterValue; } catch { return null; }
  })();
  const pinnedStrategyIds: string[] | null = (() => {
    if (slot.widgetKey !== "topbar-strategy-filter" || !savedDefault) return null;
    try {
      const p = JSON.parse(savedDefault);
      return Array.isArray(p) ? p : null;
    } catch { return null; }
  })();

  const renderContent = () => {
    switch (slot.widgetKey) {
      case "topbar-date-filter": {
        const pinnedPreset: import("../../client/utils/dateRange").DateRangePreset | null = (() => {
          if (!savedDefault) return null;
          try { const p = JSON.parse(savedDefault); return p.preset ?? null; } catch { return null; }
        })();
        return (
          <DateRangePicker
            value={dateRange}
            onChange={onDateRangeChange}
            className="h-9 text-sm"
            pinnedPreset={pinnedPreset}
            onPinPreset={onPin ? (preset) => {
              const serialized = serializeDateRange({ preset } as any);
              onPin(serialized === savedDefault ? null : serialized);
            } : undefined}
          />
        );
      }
      case "topbar-account-filter":
        return (
          <AccountGroupFilter
            accounts={accounts}
            groups={groups}
            value={accountFilter}
            onChange={onAccountFilterChange}
            className="w-[200px] h-9"
            pinnedValue={pinnedAccountFilter}
            onPinValue={onPin ? (v) => {
              const serialized = serializeAccountFilter(v);
              onPin(serialized === savedDefault ? null : serialized);
            } : undefined}
          />
        );
      case "topbar-strategy-filter":
        return strategies.length > 0 ? (
          <StrategyMultiSelect
            strategies={strategies}
            selectedIds={strategyIds}
            onChange={onStrategyIdsChange}
            className="w-[180px]"
            pinnedIds={pinnedStrategyIds ?? undefined}
            onPinIds={onPin ? (ids) => {
              const serialized = serializeStrategyIds(ids);
              onPin(serialized === savedDefault ? null : serialized);
            } : undefined}
          />
        ) : null;
      case "topbar-add-trade":
        return (
          <Button variant="default" className="h-9 text-sm" onClick={onAddTrade}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t.dashboard.addTrade}
          </Button>
        );
      case "topbar-add-payout":
        return (
          <Button variant="outline" className="h-9 text-sm" onClick={onAddPayout}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t.pages.topbarAddPayout}
          </Button>
        );
      case "topbar-pfm-filter": {
        const pinnedPfmFilter: string | null = (() => {
          if (!savedDefault) return undefined as any;
          try {
            const p = JSON.parse(savedDefault);
            return typeof p === "string" ? p : null;
          } catch { return undefined as any; }
        })();
        return (
          <TopbarPfmFilterWidget
            accounts={allAccounts ?? accounts}
            pfmTemplateFilter={pfmTemplateFilter}
            onPfmTemplateFilterChange={onPfmTemplateFilterChange ?? (() => {})}
            pinnedValue={pinnedPfmFilter}
            onPinValue={onPin ? (id) => {
              const serialized = serializePfmFilter(id);
              onPin(serialized === savedDefault ? null : serialized);
            } : undefined}
          />
        );
      }
      case "topbar-phase-filter": {
        const pinnedPhaseFilter: string | null = (() => {
          if (!savedDefault) return undefined as any;
          try {
            const p = JSON.parse(savedDefault);
            return typeof p === "string" ? p : null;
          } catch { return undefined as any; }
        })();
        return (
          <TopbarPhaseFilterWidget
            accounts={allAccounts ?? accounts}
            accountStatusFilter={accountStatusFilter}
            onAccountStatusFilterChange={onAccountStatusFilterChange ?? (() => {})}
            pinnedValue={pinnedPhaseFilter}
            onPinValue={onPin ? (status) => {
              const serialized = serializePhaseFilter(status);
              onPin(serialized === savedDefault ? null : serialized);
            } : undefined}
          />
        );
      }
      case "topbar-symbol-filter":
        return (
          <TopbarSymbolFilterWidget value={journalSymbol} onChange={onJournalSymbolChange ?? (() => {})} />
        );
      case "topbar-side-filter":
        return (
          <TopbarSideFilterWidget value={journalSide} onChange={onJournalSideChange ?? (() => {})} />
        );
      case "topbar-status-filter":
        return (
          <TopbarStatusFilterWidget value={journalStatus} onChange={onJournalStatusChange ?? (() => {})} />
        );
      case "topbar-pnl-filter":
        return (
          <TopbarPnlFilterWidget
            minPnl={journalMinPnl}
            maxPnl={journalMaxPnl}
            onMinChange={onJournalMinPnlChange ?? (() => {})}
            onMaxChange={onJournalMaxPnlChange ?? (() => {})}
          />
        );
      case "topbar-tags-filter":
        return (
          <TopbarTagsFilterWidget tags={journalTags} selectedIds={journalTagIds} onChange={onJournalTagIdsChange ?? (() => {})} />
        );
      default:
        return null;
    }
  };

  const content = renderContent();
  if (!content) return null;

  if (!isEditMode) return <>{content}</>;

  return (
    <div className="relative group flex items-center gap-1">
      {content}
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow z-10"
        aria-label="Retirer"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

export interface SortableTopbarSlotProps extends TopbarSlotRendererProps {
  id: string;
}

export function SortableTopbarSlot({ id, ...props }: SortableTopbarSlotProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  if (!props.isEditMode) {
    return <TopbarSlotRenderer {...props} slot={props.slot} />;
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1">
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground touch-none"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <TopbarSlotRenderer {...props} slot={props.slot} />
    </div>
  );
}

export interface TopbarDndWrapperProps {
  slots: SlotPlacement[];
  onSwap: (idA: string, idB: string) => void;
  children: ReactNode;
}

export function TopbarDndWrapper({ slots, onSwap, children }: TopbarDndWrapperProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onSwap(String(active.id), String(over.id));
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={slots.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}
